import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Loader2, Eye } from 'lucide-react';
import { DashboardLayout } from '@/components/layout';
import { Dropdown } from '@/components/ui';
import {
  StatsOverview,
  ClicksChart,
  DeviceChart,
  BrowserChart,
  GeoChart,
  TopLinksTable,
} from '@/components/stats';
import { useDeeplinks } from '@/hooks/useDeeplinks';
import { useStats } from '@/hooks/useStats';
import { useLinkPageStats } from '@/hooks/useLinkPageStats';

type Period = 'week' | 'month' | 'year' | 'all';

const Stats = () => {
  const { t } = useTranslation();

  const periodOptions = [
    { label: t('stats.last7Days'), value: 'week' },
    { label: t('stats.last30Days'), value: 'month' },
    { label: t('stats.lastYear'), value: 'year' },
    { label: t('stats.allTime'), value: 'all' },
  ];
  const [period, setPeriod] = useState<Period>('month');
  const { deeplinks } = useDeeplinks();
  const { stats, loading } = useStats(period);
  const { stats: lpStats, loading: lpLoading } = useLinkPageStats(undefined, period);

  // Compute derived KPIs from stats
  const derivedStats = useMemo(() => {
    if (!stats) {
      return {
        totalClicks: 0,
        clicksThisWeek: 0,
        weeklyChange: 0,
        countries: 0,
        mobilePercentage: 0,
        clicksByDay: [] as { date: string; clicks: number }[],
        clicksByCountry: [] as { country: string; clicks: number }[],
        clicksByDevice: [] as { device: string; clicks: number }[],
        clicksByBrowser: [] as { browser: string; clicks: number }[],
        topLinks: [] as { linkId: string; clicks: number; deeplink?: (typeof deeplinks)[0] }[],
      };
    }

    const countries = stats.clicksByCountry.length;

    // Calculate mobile percentage from device breakdown
    const totalDeviceClicks = stats.clicksByDevice.reduce((sum, d) => sum + d.clicks, 0);
    const mobileClicks = stats.clicksByDevice.find(
      (d) => d.device?.toLowerCase() === 'mobile'
    )?.clicks ?? 0;
    const mobilePercentage = totalDeviceClicks > 0
      ? Math.round((mobileClicks / totalDeviceClicks) * 100)
      : 0;

    // Calculate clicks this week from clicksByDay (last 7 days)
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const clicksThisWeek = stats.clicksByDay
      .filter((d) => new Date(d.date) >= weekAgo)
      .reduce((sum, d) => sum + d.clicks, 0);

    // Enrich topLinks with deeplink data
    const topLinks = stats.topLinks.map((link) => ({
      ...link,
      deeplink: deeplinks.find((d) => d.id === link.linkId),
    }));

    return {
      totalClicks: stats.totalClicks,
      clicksThisWeek,
      weeklyChange: 0,
      countries,
      mobilePercentage,
      clicksByDay: stats.clicksByDay,
      clicksByCountry: stats.clicksByCountry,
      clicksByDevice: stats.clicksByDevice,
      clicksByBrowser: stats.clicksByBrowser || [],
      topLinks,
    };
  }, [stats, deeplinks]);

  // Map linkpage views data to match existing chart interfaces (views → clicks)
  const lpDerived = useMemo(() => {
    if (!lpStats) return null;
    return {
      totalViews: lpStats.totalViews,
      viewsByDay: lpStats.viewsByDay.map((d) => ({ date: d.date, clicks: d.views })),
      viewsByCountry: lpStats.viewsByCountry.map((d) => ({ country: d.country, clicks: d.views })),
      viewsByDevice: lpStats.viewsByDevice.map((d) => ({ device: d.device, clicks: d.views })),
      viewsByBrowser: lpStats.viewsByBrowser.map((d) => ({ browser: d.browser, clicks: d.views })),
    };
  }, [lpStats]);

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">{t('stats.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {t('stats.subtitle')}
          </p>
        </div>

        <Dropdown
          items={periodOptions}
          value={period}
          onSelect={(item) => setPeriod(item.value as Period)}
          placeholder={t('stats.selectPeriod')}
        />
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-lavender-500 animate-spin" />
        </div>
      )}

      {!loading && (
        <>
          {/* Overview Stats */}
          <div className="mb-8">
            <StatsOverview
              totalClicks={derivedStats.totalClicks}
              clicksThisWeek={derivedStats.clicksThisWeek}
              weeklyChange={derivedStats.weeklyChange}
              countries={derivedStats.countries}
              mobilePercentage={derivedStats.mobilePercentage}
              totalConversions={stats?.totalConversions}
              conversionRate={stats?.conversionRate}
              totalRevenue={stats?.totalRevenue}
            />
          </div>

          {/* Charts Grid */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <div className="lg:col-span-2">
              <ClicksChart data={derivedStats.clicksByDay} />
            </div>
            <DeviceChart data={derivedStats.clicksByDevice} />
            <BrowserChart data={derivedStats.clicksByBrowser} />
            <GeoChart data={derivedStats.clicksByCountry} />
          </div>

          {/* Top Links */}
          <TopLinksTable links={derivedStats.topLinks} />

          {/* Link Page Views Section */}
          {lpLoading && (
            <div className="flex items-center justify-center py-12 mt-8">
              <Loader2 className="w-6 h-6 text-lavender-500 animate-spin" />
            </div>
          )}

          {!lpLoading && lpDerived && lpDerived.totalViews > 0 && (
            <>
              {/* Section Header */}
              <div className="flex items-center gap-3 mt-10 mb-6">
                <div className="w-10 h-10 bg-lavender-100 dark:bg-lavender-900/30 rounded-xl flex items-center justify-center">
                  <Eye className="w-5 h-5 text-lavender-600 dark:text-lavender-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-dark-800 dark:text-white">
                    {t('stats.linkPageViews')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('stats.totalViews')}: {lpDerived.totalViews.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* LinkPage Charts */}
              <div className="grid lg:grid-cols-2 gap-6 mb-6">
                <div className="lg:col-span-2">
                  <ClicksChart data={lpDerived.viewsByDay} title={t('stats.viewsOverTime')} />
                </div>
                <DeviceChart data={lpDerived.viewsByDevice} title={t('stats.viewsByDevice')} />
                <BrowserChart data={lpDerived.viewsByBrowser} title={t('stats.viewsByBrowser')} />
                <GeoChart data={lpDerived.viewsByCountry} title={t('stats.topViewCountries')} />
              </div>
            </>
          )}

          {/* Empty state info */}
          {derivedStats.totalClicks === 0 && (
            <div className="mt-8 text-center p-8 bg-lavender-50 dark:bg-lavender-900/20 rounded-3xl">
              <div className="w-16 h-16 bg-lavender-100 dark:bg-lavender-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-lavender-600 dark:text-lavender-400" />
              </div>
              <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-2">
                {t('stats.noAnalyticsYet')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
                {t('stats.noAnalyticsMessage')}
              </p>
              <p className="text-sm text-gray-400">
                {t('stats.analyticsRealTime')}
              </p>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default Stats;
